import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router';
import { api } from '../api';
import { canWrite, useAuth } from '../auth';
import { Badge, Button, Card, Checkbox, Empty, ErrorMessage, Field, Input, Notice, PageHeader, Select, Spinner, Stat, money, number, plural, useConfirm } from '../ui';

export interface BillRow {
  id: number;
  number: string;
  supplierId: number | null;
  supplierName: string | null;
  purchaseId: number | null;
  purchaseDescription: string | null;
  billDate: string;
  dueOn: string | null;
  reference: string | null;
  currency: string;
  amount: string;
  paid: string;
  outstanding: string;
  status: 'open' | 'part_paid' | 'paid' | 'void';
  overdue: boolean;
  daysOverdue: number;
}

const STATUS = { open: ['Open', 'blue'], part_paid: ['Part paid', 'amber'], paid: ['Paid', 'green'], void: ['Void', 'slate'] } as const;
const today = () => new Date().toISOString().slice(0, 10);

export function BillStatus({ b }: { b: BillRow }) {
  return (
    <span className="inline-flex items-center gap-1">
      <Badge tone={STATUS[b.status][1]}>{STATUS[b.status][0]}</Badge>
      {b.overdue && <Badge tone="red">{b.daysOverdue} days late</Badge>}
    </span>
  );
}

/** Supplier bills, what is owed and what is still to be billed. */
export function Bills() {
  const { user } = useAuth();
  const writable = canWrite(user);
  const [params, setParams] = useSearchParams();
  const tab = (['open', 'overdue', 'paid', 'unbilled'].includes(params.get('tab') ?? '') ? params.get('tab') : 'open') as 'open' | 'overdue' | 'paid' | 'unbilled';
  const summary = useQuery({
    queryKey: ['bills-summary'],
    queryFn: () => api<{ ageing: { band: string; bills: number; owed: string }[]; suppliers: { supplierId: number | null; name: string; bills: number; owed: string }[]; unbilled: number }>('/api/v1/bills/summary'),
  });
  const list = useQuery({ queryKey: ['bills', tab], enabled: tab !== 'unbilled', queryFn: () => api<{ bills: BillRow[] }>(`/api/v1/bills?filter=${tab}`).then((r) => r.bills) });
  const unbilled = useQuery({
    queryKey: ['bills-unbilled'],
    enabled: tab === 'unbilled',
    queryFn: () => api<{ purchases: { purchaseId: number; description: string; purchasedOn: string; currency: string; total: string; supplierName: string | null }[] }>('/api/v1/bills/unbilled').then((r) => r.purchases),
  });
  const owed = summary.data?.ageing.reduce((t, a) => t + Number(a.owed), 0) ?? 0;
  const late = summary.data?.ageing.filter((a) => a.band !== 'Not yet due').reduce((t, a) => t + Number(a.owed), 0) ?? 0;

  return (
    <>
      <PageHeader title="Bills" subtitle="What suppliers have billed you, what you have paid, and which orders have no bill yet." />
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          <Stat label="You owe" value={money(owed.toFixed(2))} note="Unpaid bills, in your currency" />
          <Stat label="Of which late" value={money(late.toFixed(2))} note="Past their due date" />
          <Stat label="Orders with no bill" value={number(summary.data?.unbilled ?? 0)} note="Bills still to enter" />
          <Stat label="Suppliers owed" value={number(summary.data?.suppliers.length ?? 0)} note={summary.data?.suppliers[0] ? `Most: ${summary.data.suppliers[0].name}` : ' '} />
        </div>
        <div role="tablist" className="flex flex-wrap gap-1 border-b border-slate-200">
          {(
            [
              ['open', 'To pay'],
              ['overdue', 'Late'],
              ['paid', 'Paid'],
              ['unbilled', 'Orders with no bill'],
            ] as const
          ).map(([k, label]) => (
            <button key={k} role="tab" aria-selected={tab === k} type="button" onClick={() => setParams({ tab: k })} className={`-mb-px border-b-2 px-3 py-2 text-sm ${tab === k ? 'border-slate-900 font-medium' : 'border-transparent text-slate-600 hover:text-slate-900'}`}>
              {label}
            </button>
          ))}
        </div>
        {tab !== 'unbilled' && (
          <Card>
            {list.isPending && <Spinner />}
            {list.error && <ErrorMessage error={list.error} />}
            {list.data && list.data.length === 0 && <Empty>Nothing here.</Empty>}
            {list.data && list.data.length > 0 && (
              <div className="overflow-x-auto">
                <table className="data w-full min-w-[44rem] text-sm">
                  <thead>
                    <tr>
                      <th>Bill</th>
                      <th>Supplier</th>
                      <th>Their ref</th>
                      <th>Billed</th>
                      <th>Due</th>
                      <th>Status</th>
                      <th className="text-right">Amount</th>
                      <th className="text-right">Owing</th>
                    </tr>
                  </thead>
                  <tbody>
                    {list.data.map((b) => (
                      <tr key={b.id}>
                        <td>
                          <Link to={`/bills/${b.id}`} className="font-medium hover:underline">
                            {b.number}
                          </Link>
                        </td>
                        <td>{b.supplierName ?? '—'}</td>
                        <td>{b.reference ?? '—'}</td>
                        <td>{b.billDate}</td>
                        <td>{b.dueOn ?? '—'}</td>
                        <td>
                          <BillStatus b={b} />
                        </td>
                        <td className="text-right tabular-nums">{money(b.amount, b.currency)}</td>
                        <td className="text-right tabular-nums">{money(b.outstanding, b.currency)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        )}
        {tab === 'unbilled' && (
          <Card>
            {unbilled.isPending && <Spinner />}
            {unbilled.data && unbilled.data.length === 0 && <Empty>Every purchase has a bill.</Empty>}
            {unbilled.data && unbilled.data.length > 0 && (
              <div className="overflow-x-auto">
                <table className="data w-full min-w-[40rem] text-sm">
                  <thead>
                    <tr>
                      <th>Purchase</th>
                      <th>Supplier</th>
                      <th>Date</th>
                      <th className="text-right">Total</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {unbilled.data.map((p) => (
                      <tr key={p.purchaseId}>
                        <td>
                          <Link to={`/purchases/${p.purchaseId}`} className="font-medium hover:underline">
                            {p.description || `Purchase ${p.purchaseId}`}
                          </Link>
                        </td>
                        <td>{p.supplierName ?? '—'}</td>
                        <td>{p.purchasedOn}</td>
                        <td className="text-right tabular-nums">{money(p.total, p.currency)}</td>
                        <td className="text-right">{writable && <Link to={`/purchases/${p.purchaseId}`} className="text-sm text-blue-700 hover:underline">Open to bill it</Link>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        )}
      </div>
    </>
  );
}

interface BillDetailData {
  bill: BillRow;
  payments: { id: number; paidOn: string; method: string | null; reference: string | null; amount: string }[];
}

export function BillDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const writable = canWrite(user);
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { confirm, dialog } = useConfirm();
  const q = useQuery({ queryKey: ['bill', id], queryFn: () => api<BillDetailData>(`/api/v1/bills/${id}`) });
  const [amount, setAmount] = useState('');
  const [paidOn, setPaidOn] = useState(today());
  const [method, setMethod] = useState('Bank transfer');
  const [ref, setRef] = useState('');
  const refresh = () => {
    for (const k of ['bill', 'bills', 'bills-summary', 'bills-unbilled', 'purchase-payables']) void qc.invalidateQueries({ queryKey: [k] });
  };
  const pay = useMutation({
    mutationFn: () => api('/api/v1/payments', { body: { paidOn, method, reference: ref || null, currency: q.data!.bill.currency, applications: [{ billId: Number(id), amount }] } }),
    onSuccess: () => {
      setAmount('');
      refresh();
    },
  });
  const voided = useMutation({ mutationFn: () => api(`/api/v1/bills/${id}/void`, { method: 'POST', body: {} }), onSuccess: refresh });
  if (q.isPending) return <Spinner />;
  if (q.error || !q.data) return <ErrorMessage error={q.error} />;
  const { bill, payments } = q.data;
  const open = bill.status === 'open' || bill.status === 'part_paid';
  return (
    <>
      {dialog}
      <PageHeader
        title={bill.number}
        subtitle={
          <>
            <Link to="/bills" className="hover:underline">
              Bills
            </Link>{' '}
            / {bill.supplierName ?? 'no supplier'}
            {bill.purchaseId ? (
              <>
                {' '}
                ·{' '}
                <Link to={`/purchases/${bill.purchaseId}`} className="hover:underline">
                  {bill.purchaseDescription || `Purchase ${bill.purchaseId}`}
                </Link>
              </>
            ) : null}
          </>
        }
        actions={<BillStatus b={bill} />}
      />
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          <Stat label="Amount" value={money(bill.amount, bill.currency)} note={bill.reference ? `Their ref ${bill.reference}` : 'Billed ' + bill.billDate} />
          <Stat label="Paid" value={money(bill.paid, bill.currency)} />
          <Stat label="Outstanding" value={money(bill.outstanding, bill.currency)} />
          <Stat label="Due" value={bill.dueOn ?? '—'} note={bill.overdue ? `${bill.daysOverdue} days late` : undefined} />
        </div>
        <Card title="Payments">
          {payments.length === 0 && <p className="text-sm text-slate-500">No payments yet.</p>}
          {payments.length > 0 && (
            <ul className="divide-y divide-slate-100 text-sm">
              {payments.map((p) => (
                <li key={p.id} className="flex justify-between gap-3 py-1.5">
                  <span>
                    {p.paidOn} · {p.method ?? 'payment'}
                    {p.reference ? ` · ${p.reference}` : ''}
                  </span>
                  <span className="tabular-nums">{money(p.amount, bill.currency)}</span>
                </li>
              ))}
            </ul>
          )}
          {writable && open && (
            <form
              className="mt-4 flex flex-wrap items-end gap-3 rounded-md bg-slate-50 p-3"
              onSubmit={(e) => {
                e.preventDefault();
                pay.mutate();
              }}
            >
              <Field label="Amount paid">
                <Input inputMode="decimal" className="w-28" placeholder={bill.outstanding} value={amount} onChange={(e) => setAmount(e.target.value)} />
              </Field>
              <Field label="Date">
                <Input type="date" value={paidOn} onChange={(e) => setPaidOn(e.target.value)} />
              </Field>
              <Field label="Method">
                <Select value={method} onChange={(e) => setMethod(e.target.value)}>
                  {['Bank transfer', 'Card', 'Cash', 'PayPal', 'Other'].map((m) => (
                    <option key={m}>{m}</option>
                  ))}
                </Select>
              </Field>
              <Field label="Reference">
                <Input value={ref} onChange={(e) => setRef(e.target.value)} />
              </Field>
              <Button type="submit" disabled={pay.isPending || !(Number(amount) > 0)}>
                Record payment
              </Button>
              <Button type="button" variant="ghost" onClick={() => setAmount(bill.outstanding)}>
                Pay it all
              </Button>
              <div className="basis-full">
                <ErrorMessage error={pay.error} />
              </div>
            </form>
          )}
          {writable && open && payments.length === 0 && (
            <div className="mt-3">
              <Button
                variant="ghost"
                small
                onClick={async () => {
                  if (await confirm(`Void ${bill.number}? It is taken out of what you owe. You can only void a bill before paying it.`, { label: 'Void it', danger: true })) {
                    await voided.mutateAsync();
                    navigate('/bills');
                  }
                }}
              >
                Void this bill
              </Button>
              <ErrorMessage error={voided.error} />
            </div>
          )}
        </Card>
      </div>
    </>
  );
}

interface PayablesData {
  billable: boolean;
  currency: string;
  orderTotal: string;
  billed: string;
  paid: string;
  outstanding: string;
  variance: string;
  unbilled: string;
  bills: BillRow[];
}

/** On a purchase: ordered, billed and paid side by side, with the bills and a way to make one. */
export function PurchasePayables({ purchaseId, writable }: { purchaseId: number; writable: boolean }) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['purchase-payables', purchaseId], queryFn: () => api<PayablesData>(`/api/v1/purchases/${purchaseId}/payables`) });
  const [adding, setAdding] = useState(false);
  const [billDate, setBillDate] = useState(today());
  const [dueOn, setDueOn] = useState('');
  const [reference, setReference] = useState('');
  const [amount, setAmount] = useState('');
  const [paid, setPaid] = useState(false);
  const [method, setMethod] = useState('Card');
  const make = useMutation({
    mutationFn: () => api('/api/v1/bills', { body: { purchaseId, billDate, dueOn: dueOn || null, reference: reference || null, ...(amount ? { amount } : {}), ...(paid ? { paid: { paidOn: billDate, method } } : {}) } }),
    onSuccess: () => {
      setAdding(false);
      for (const k of ['purchase-payables', 'bills', 'bills-summary', 'bills-unbilled']) void qc.invalidateQueries({ queryKey: [k] });
    },
  });
  if (q.isPending || !q.data || !q.data.billable) return null;
  const d = q.data;
  return (
    <Card
      title="Bills and payments"
      actions={writable && !adding ? <Button small variant="secondary" onClick={() => setAdding(true)}>Create bill</Button> : undefined}
    >
      <div className="grid grid-cols-2 gap-3 text-sm sm:grid-cols-4">
        <Stat label="Ordered" value={money(d.orderTotal, d.currency)} />
        <Stat label="Billed" value={money(d.billed, d.currency)} note={Number(d.variance) !== 0 && Number(d.billed) > 0 ? `${Number(d.variance) > 0 ? '+' : ''}${money(d.variance, d.currency)} against the order` : undefined} />
        <Stat label="Paid" value={money(d.paid, d.currency)} />
        <Stat label="Owing" value={money(d.outstanding, d.currency)} />
      </div>
      {d.bills.length > 0 && (
        <ul className="mt-3 divide-y divide-slate-100 text-sm">
          {d.bills.map((b) => (
            <li key={b.id} className="flex flex-wrap items-center justify-between gap-2 py-1.5">
              <Link to={`/bills/${b.id}`} className="font-medium hover:underline">
                {b.number}
                {b.reference ? ` · ${b.reference}` : ''}
              </Link>
              <span className="flex items-center gap-2">
                <BillStatus b={b} />
                <span className="tabular-nums">{money(b.amount, b.currency)}</span>
              </span>
            </li>
          ))}
        </ul>
      )}
      {d.bills.length === 0 && !adding && <Notice tone="amber">No bill has been entered for this purchase. If you paid at the till or at checkout, create the bill and tick paid.</Notice>}
      {adding && (
        <form
          className="mt-3 flex flex-wrap items-end gap-3 rounded-md bg-slate-50 p-3"
          onSubmit={(e) => {
            e.preventDefault();
            make.mutate();
          }}
        >
          <Field label="Bill date">
            <Input type="date" value={billDate} onChange={(e) => setBillDate(e.target.value)} />
          </Field>
          <Field label="Due">
            <Input type="date" value={dueOn} onChange={(e) => setDueOn(e.target.value)} />
          </Field>
          <Field label="Their invoice no.">
            <Input value={reference} onChange={(e) => setReference(e.target.value)} />
          </Field>
          <Field label={`Amount (${money(d.orderTotal, d.currency)} if blank)`}>
            <Input inputMode="decimal" className="w-28" value={amount} onChange={(e) => setAmount(e.target.value)} />
          </Field>
          <div className="pb-2">
            <Checkbox label="Already paid" checked={paid} onChange={setPaid} />
          </div>
          {paid && (
            <Field label="Paid by">
              <Select value={method} onChange={(e) => setMethod(e.target.value)}>
                {['Card', 'Cash', 'PayPal', 'Bank transfer', 'Other'].map((m) => (
                  <option key={m}>{m}</option>
                ))}
              </Select>
            </Field>
          )}
          <Button type="submit" disabled={make.isPending}>
            Save bill
          </Button>
          <Button type="button" variant="ghost" onClick={() => setAdding(false)}>
            Cancel
          </Button>
          <div className="basis-full">
            <ErrorMessage error={make.error} />
          </div>
        </form>
      )}
    </Card>
  );
}

void plural;
